import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CoreDataService, StorageManagerService } from '@core/services';
import * as Sentry from '@sentry/angular';
import { Observable, switchMap, tap, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

export interface AuthMeta {
  is_authenticated?: boolean;
  session_token?: string;
}

@Injectable()
export class AuthInterceptorService implements HttpInterceptor {
  constructor(
    private storageManagerService: StorageManagerService,
    private coreDataService: CoreDataService,
  ) { }

  private handleAuthMeta(meta: AuthMeta): void {
    if (!meta) {
      return;
    }

    // Handle authentication status first, as it affects token handling
    const authStatus = meta.is_authenticated;
    const token = meta.session_token;

    if (authStatus === false) {
      const currentAuthStatus = this.storageManagerService.load('authenticated');
      if (currentAuthStatus !== 'false') {
        this.storageManagerService.store('authenticated', 'false');
        // Clear token when explicitly marked as not authenticated (unless new token provided)
        if (!token) {
          this.storageManagerService.delete('token');
        }
      }
    } else if (authStatus === true) {
      const currentAuthStatus = this.storageManagerService.load('authenticated');
      if (currentAuthStatus !== 'true') {
        this.storageManagerService.store('authenticated', 'true');
      }
    }
    // If token is provided, no matter the authentication status, we should update it to ensure we have the latest token
    if (token) {
      this.storageManagerService.store('token', token);
    }
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Exclude requests to local assets
    if (request.url.startsWith('assets') || request.url.startsWith('https://wipark.azurefd.net/v1/marketingagreement')) {
      return next.handle(request);
    }

    let headers: { [key: string]: string } = {};

    try {
      const token = this.storageManagerService.load('token');
      if (token) {
        headers['Authorization'] = `Token ${token}`;
      }
    } catch (err) {
      Sentry.captureException(err);
    }

    try {
      headers['Accept-Language'] = this.storageManagerService.load('language') ?? 'en';
    } catch (err) {
      Sentry.captureException(err);
    }

    request = request.clone({
      setHeaders: headers,
    });

    return next.handle(request).pipe(
      tap((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          this.handleAuthMeta(event.body?.meta);
        }
      }),
      catchError((error: HttpErrorResponse) => {
        this.handleAuthMeta(error.error?.meta);

        // On 401/403 errors, fetchCoreData will handle logout if needed
        // This ensures proper cleanup and user notification
        if (
          (error.status === 401 || error.status === 403) &&
          !(
            request.url.includes('/api/user/auth/app/v1/auth') ||
            request.url.includes('/api/user/auth/browser/confirm_login') ||
            request.url.includes('/api/user/v2/auth/register') ||
            request.url.includes('/api/user/v2/user/welcome_email_from_token') ||
            request.url.includes('/api/backoffice/v2/customer_portal/core')
          )
        ) {
          return this.coreDataService.fetchCoreData().pipe(switchMap(() => throwError(() => error)));
        }
        return throwError(() => error);
      }),
    );
  }
}
